import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  RefreshCw,
  Send,
  ArrowLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { formatDate } from '@/utils/formatters';
import type {
  Drawdown,
  DrawdownGroup,
  DrawdownIssueValidation,
} from '@/types/drawdown';
import { getDrawdownStatusBadgeClass } from './drawdownPreviewStatus';
import { DrawdownActionsMenu } from './DrawdownActionsMenu';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface DrawdownPreviewHeaderProps {
  drawdown?: Drawdown;
  issueValidation?: DrawdownIssueValidation | null;
  fundId: string;
  drawdownId: string;
  onStatusUpdate?: (drawdownId: string, status: string) => Promise<void>;
  isUpdatingStatus?: boolean;
  docsGenerating?: boolean;
  sendEmailsGroup?: DrawdownGroup | null;
  canSendEmails?: boolean;
  backTo?: string;
  breadcrumbs?: BreadcrumbItem[];
}

function buildIssueBlockedParts(validation: DrawdownIssueValidation): string {
  const parts: string[] = [];
  if (validation.missing_investor_iban_count > 0) {
    parts.push(`${validation.missing_investor_iban_count} missing IBAN`);
  }
  if (validation.missing_reference_number_count > 0) {
    parts.push(
      `${validation.missing_reference_number_count} missing payment reference`
    );
  }
  if (
    validation.total_investors > 0 &&
    !validation.has_capital_call_recipient
  ) {
    parts.push('no capital-call email');
  }
  return parts.join(' · ');
}

function buildIssueBlockedTitle(
  investorGaps: string,
  blockingTransferCount: number
): string {
  const reasons: string[] = [];
  if (investorGaps) reasons.push(investorGaps);
  if (blockingTransferCount > 0) {
    reasons.push(
      `dated before ${blockingTransferCount} LP transfer${blockingTransferCount === 1 ? '' : 's'}`
    );
  }
  if (reasons.length === 0) return 'Cannot issue.';
  return `Cannot issue: ${reasons.join('; ')}.`;
}

export function DrawdownPreviewHeader({
  drawdown,
  issueValidation,
  fundId,
  drawdownId,
  onStatusUpdate,
  isUpdatingStatus = false,
  docsGenerating = false,
  sendEmailsGroup = null,
  canSendEmails = false,
  backTo,
  breadcrumbs,
}: DrawdownPreviewHeaderProps) {
  const navigate = useNavigate();
  const issueBlocked =
    drawdown?.status?.toLowerCase() === 'draft' && issueValidation
      ? !issueValidation.can_issue
      : false;
  const investorGaps = issueValidation
    ? buildIssueBlockedParts(issueValidation)
    : '';

  const subline = drawdown
    ? [
        'Capital call',
        `${drawdown.batch_count} ${drawdown.batch_count === 1 ? 'batch' : 'batches'}`,
      ].join(' · ')
    : null;

  return (
    <div className="space-y-2">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          {backTo && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backTo)}
              className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
          )}
          {breadcrumbs.map((crumb, index) => (
            <span key={crumb.label} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="h-3.5 w-3.5" />}
              {crumb.href ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(crumb.href!)}
                  className="h-auto p-0 text-muted-foreground hover:text-foreground"
                >
                  {crumb.label}
                </Button>
              ) : (
                <span>{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground">
              {drawdown?.purpose
                ? `Drawdown - ${drawdown.purpose}`
                : 'Drawdown Preview'}
            </h1>
            {drawdown?.status && (
              <Badge
                role="status"
                variant="outline"
                className={`uppercase tracking-wide text-[11px] font-semibold rounded-full ${getDrawdownStatusBadgeClass(drawdown.status)}`}
              >
                {drawdown.status}
              </Badge>
            )}
          </div>
          {subline && (
            <p className="text-sm text-muted-foreground mt-1">{subline}</p>
          )}
          {drawdown?.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {drawdown.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onStatusUpdate && drawdown?.status?.toLowerCase() === 'draft' && (
            <Button
              onClick={() => onStatusUpdate(drawdownId, 'issued')}
              size="sm"
              disabled={isUpdatingStatus || issueBlocked}
              title={
                issueBlocked && issueValidation
                  ? buildIssueBlockedTitle(
                      investorGaps,
                      issueValidation.blocking_transfers.length
                    )
                  : undefined
              }
            >
              {isUpdatingStatus ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isUpdatingStatus ? 'Issuing...' : 'Issue Drawdown'}
            </Button>
          )}
          {drawdown && (
            <DrawdownActionsMenu
              fundId={fundId}
              drawdownId={drawdownId}
              drawdownName={drawdown.purpose || 'this drawdown'}
              docsGenerating={docsGenerating}
              sendEmailsGroup={sendEmailsGroup}
              canSendEmails={canSendEmails}
              backTo={backTo}
            />
          )}
        </div>
      </div>
      {issueBlocked && issueValidation && investorGaps !== '' && (
        <div
          role="status"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span>
            <strong className="font-semibold text-destructive">
              {issueValidation.blocking_items.length} investor
              {issueValidation.blocking_items.length === 1 ? '' : 's'}
            </strong>{' '}
            need data before issuing - {investorGaps}
          </span>
        </div>
      )}
      {issueBlocked &&
        issueValidation?.blocking_transfers.map((transfer) => (
          <div
            key={transfer.fund_transaction_id}
            role="status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
            <span>
              Dated before{' '}
              <Link
                to={`/app/funds/${fundId}/fund-transactions/${transfer.fund_transaction_id}`}
                className="font-semibold text-destructive underline"
              >
                {transfer.description}
              </Link>{' '}
              ({transfer.status.toLowerCase()}, effective{' '}
              {formatDate(transfer.effective_date)}), which moves{' '}
              {transfer.transferor_name}&apos;s commitment. Date the call on or
              after the transfer
              {transfer.status === 'DRAFT' && ', or delete the transfer first'}.
            </span>
          </div>
        ))}
    </div>
  );
}
